// pricing/pricing.schema.ts
import { z } from "zod";
var PriceListTypeSchema = z.enum(["default", "sale", "override"]);
var PriceListStatusSchema = z.enum(["draft", "active", "expired"]);
var PriceExternalTypeSchema = z.enum(["product_price", "product_discount", "customer_price"]);
var PriceListDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  type: PriceListTypeSchema,
  status: PriceListStatusSchema,
  startsAt: z.string().datetime().nullable(),
  endsAt: z.string().datetime().nullable(),
  priority: z.number().int(),
  externalId: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var CreatePriceListInputSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().nullable().optional(),
  type: PriceListTypeSchema.default("default"),
  status: PriceListStatusSchema.default("active"),
  startsAt: z.string().datetime().nullable().optional(),
  endsAt: z.string().datetime().nullable().optional(),
  priority: z.number().int().default(0),
  externalId: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var UpdatePriceListInputSchema = CreatePriceListInputSchema.partial();
var UpsertPriceListInputSchema = z.object({
  externalId: z.string(),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  type: PriceListTypeSchema.default("default"),
  status: PriceListStatusSchema.default("active"),
  startsAt: z.string().datetime().nullable().optional(),
  endsAt: z.string().datetime().nullable().optional(),
  priority: z.number().int().default(0),
  metadata: z.record(z.unknown()).optional()
});
var PriceRuleAttributeSchema = z.enum(["buyer_organization_id", "customer_uuid"]);
var PriceRuleOperatorSchema = z.enum(["eq"]);
var PriceDtoSchema = z.object({
  id: z.string().uuid(),
  productVariantId: z.string().uuid(),
  priceListId: z.string().uuid().nullable(),
  currencyCode: z.string().length(3),
  amount: z.number().nonnegative(),
  minQuantity: z.number().int().positive().nullable(),
  maxQuantity: z.number().int().positive().nullable(),
  customerId: z.number().int().positive().nullable(),
  externalId: z.string().nullable(),
  externalType: PriceExternalTypeSchema.nullable(),
  rulesCount: z.number().int().nonnegative(),
  startsAt: z.string().datetime().nullable(),
  endsAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  createdByMemberId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var PriceRuleDtoSchema = z.object({
  id: z.string().uuid(),
  priceId: z.string().uuid(),
  attribute: PriceRuleAttributeSchema,
  operator: PriceRuleOperatorSchema,
  value: z.string().min(1),
  priority: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var PriceWithRulesDtoSchema = PriceDtoSchema.extend({
  rules: z.array(PriceRuleDtoSchema)
});
var CreatePriceRuleInputSchema = z.object({
  attribute: PriceRuleAttributeSchema,
  operator: PriceRuleOperatorSchema.default("eq"),
  value: z.string().min(1),
  priority: z.number().int().default(0)
});
var CreatePriceInputSchema = z.object({
  productVariantId: z.string().uuid(),
  priceListId: z.string().uuid().nullable().optional(),
  currencyCode: z.string().length(3).default("USD"),
  amount: z.number().nonnegative(),
  minQuantity: z.number().int().positive().nullable().optional(),
  maxQuantity: z.number().int().positive().nullable().optional(),
  customerId: z.number().int().positive().nullable().optional(),
  externalId: z.string().nullable().optional(),
  externalType: PriceExternalTypeSchema.nullable().optional(),
  startsAt: z.string().datetime().nullable().optional(),
  endsAt: z.string().datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdByMemberId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var UpdatePriceInputSchema = z.object({
  amount: z.number().nonnegative().optional(),
  minQuantity: z.number().int().positive().nullable().optional(),
  maxQuantity: z.number().int().positive().nullable().optional(),
  startsAt: z.string().datetime().nullable().optional(),
  endsAt: z.string().datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var CreatePriceWithRulesInputSchema = z.object({
  price: CreatePriceInputSchema,
  rules: z.array(CreatePriceRuleInputSchema).min(1)
});
var UpsertPriceInputSchema = z.object({
  productVariantId: z.string().uuid(),
  priceListId: z.string().uuid().nullable().optional(),
  currencyCode: z.string().length(3).default("USD"),
  amount: z.number().nonnegative(),
  minQuantity: z.number().int().positive().nullable().optional(),
  maxQuantity: z.number().int().positive().nullable().optional(),
  customerId: z.number().int().positive().nullable().optional(),
  externalId: z.string().nullable().optional(),
  externalType: PriceExternalTypeSchema.nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var PriceTierSchema = z.object({
  minQuantity: z.number().int().positive(),
  maxQuantity: z.number().int().positive().nullable(),
  amount: z.number().nonnegative(),
  priceListId: z.string().uuid().nullable()
});
var VariantPricingSchema = z.object({
  productVariantId: z.string().uuid(),
  basePrice: z.number().nonnegative().nullable(),
  bulkTiers: z.array(PriceTierSchema),
  customerPrice: z.number().nonnegative().nullable()
});
var PricingSyncStatsSchema = z.object({
  basePricesCreated: z.number().int(),
  basePricesUpdated: z.number().int(),
  bulkDiscountsCreated: z.number().int(),
  bulkDiscountsUpdated: z.number().int(),
  customerPricesCreated: z.number().int(),
  customerPricesUpdated: z.number().int(),
  errors: z.number().int()
});
var TierLadderItemSchema = z.object({
  amount: z.number().nonnegative(),
  minQuantity: z.number().int().positive().nullable().optional(),
  maxQuantity: z.number().int().positive().nullable().optional(),
  // rejected non-null today; here for forward compat
  startsAt: z.string().datetime().nullable().optional(),
  endsAt: z.string().datetime().nullable().optional(),
  notes: z.string().nullable().optional()
});
var SetTiersForScopeScopeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("global") }),
  z.object({
    kind: z.literal("org"),
    buyerOrganizationId: z.string().uuid()
  }),
  z.object({
    kind: z.literal("customer"),
    buyerOrganizationId: z.string().uuid(),
    customerUuid: z.string().uuid()
  })
]);
var SetTiersForScopeRequestSchema = z.object({
  scope: SetTiersForScopeScopeSchema,
  currencyCode: z.string().length(3).optional(),
  tiers: z.array(TierLadderItemSchema).min(1),
  /**
   * Optional metadata applied to every rung. Use `metadata.source = '<scriptname>'`
   * to mark system-authored ladders (e.g. backfills) so they're distinguishable
   * from supplier-authored ladders in reporting.
   */
  metadata: z.record(z.unknown()).optional()
});
export {
  CreatePriceInputSchema,
  CreatePriceListInputSchema,
  CreatePriceRuleInputSchema,
  CreatePriceWithRulesInputSchema,
  PriceDtoSchema,
  PriceExternalTypeSchema,
  PriceListDtoSchema,
  PriceListStatusSchema,
  PriceListTypeSchema,
  PriceRuleAttributeSchema,
  PriceRuleDtoSchema,
  PriceRuleOperatorSchema,
  PriceTierSchema,
  PriceWithRulesDtoSchema,
  PricingSyncStatsSchema,
  SetTiersForScopeRequestSchema,
  UpdatePriceInputSchema,
  UpdatePriceListInputSchema,
  UpsertPriceInputSchema,
  UpsertPriceListInputSchema,
  VariantPricingSchema
};
